import type { ProductBacklogListResponse } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { AlertTriangle, CloudOff, OctagonAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { describeLayoutIssue, formatReadAt } from '../../lib/product-backlog-view.js';

/**
 * 帯の色。red＝ずれ、amber＝区切りの形が変わった、gray＝読めない。
 * @graph-connects none
 */
type Tone = 'red' | 'amber' | 'gray';

/** 色ごとの見た目。 @graph-connects none */
const TONE_CLASS: Record<Tone, string> = {
  red: 'border-[var(--color-error)]/25 bg-[var(--color-error-muted)] text-[var(--color-error)]',
  amber: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  gray: 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]',
};

/**
 * 帯1本（何が問題か → 次の一手）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の上に出す帯1本を、問題の1行と次の一手で出す
 * @graph-connects none
 */
function Band({
  tone,
  icon,
  message,
  next,
}: {
  tone: Tone;
  icon: ReactNode;
  message: string;
  next: ReactNode;
}) {
  return (
    <div
      role={tone === 'gray' ? 'status' : 'alert'}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-sm ${TONE_CLASS[tone]}`}
    >
      <span className="flex min-w-0 items-center gap-2 font-medium">
        {icon}
        <span>{message}</span>
      </span>
      <span className="flex items-center gap-2 text-[var(--color-text-secondary)]">{next}</span>
    </div>
  );
}

/**
 * 一覧の上の帯（問題があるときだけ）。赤（ずれ）を上に、黄（区切りの形）・灰（読めない）の順に出す。
 * スプシが読めないときは、読めなかった時刻と理由と［最新を読み込む］を出す。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の上に、書き写しのずれ（赤）・スプシの区切りの形の変化（黄）・スプシや Backlog の値が読めないこと（灰）を、何が問題か→次の一手の帯で出す
 * @graph-connects Band [calls] 帯1本
 * @graph-connects describeLayoutIssue [calls] 形の問題の説明
 * @graph-connects formatReadAt [calls] 読んだ時刻の書き方
 */
export function ProductBacklogAlerts({
  data,
  now,
  onReload,
  reloading,
}: {
  data: ProductBacklogListResponse;
  now: Date;
  onReload: () => void;
  reloading: boolean;
}) {
  const { t } = useTranslation();
  const reloadButton = (
    <Button size="sm" variant="secondary" onClick={onReload} disabled={reloading}>
      {t('productBacklog.reload')}
    </Button>
  );

  if (data.sheetStatus === 'unreadable') {
    return (
      <div className="mb-3 flex flex-col gap-2">
        <Band
          tone="gray"
          icon={<CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />}
          message={t('productBacklog.alert.sheetUnreadable', {
            time: formatReadAt(data.readAt, now),
          })}
          next={
            <>
              <span>{t(`productBacklog.alert.reason.${data.reason}`)}</span>
              {reloadButton}
            </>
          }
        />
      </div>
    );
  }

  const [drift, ...otherDrifts] = data.drifts;
  const [issue, ...otherIssues] = data.layoutIssues;
  const bands: ReactNode[] = [];
  if (drift !== undefined) {
    bands.push(
      <Band
        key="drift"
        tone="red"
        icon={<OctagonAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
        message={`${t('productBacklog.alert.drift', {
          title: drift.title ?? drift.sheetKey,
          row: String(drift.rowNumber),
        })}${otherDrifts.length > 0 ? ` ${t('productBacklog.alert.driftMore', { n: String(otherDrifts.length) })}` : ''}`}
        next={<span>{t('productBacklog.alert.driftNext')}</span>}
      />,
    );
  }
  if (issue !== undefined) {
    const detail = `${describeLayoutIssue(issue, t)}${otherIssues.length > 0 ? ` ${t('productBacklog.alert.layoutMore', { n: String(otherIssues.length) })}` : ''}`;
    bands.push(
      <Band
        key="layout"
        tone="amber"
        icon={<AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />}
        message={t('productBacklog.alert.layout', { detail })}
        next={<span>{t('productBacklog.alert.layoutNext')}</span>}
      />,
    );
  }
  if (data.backlogStatus === 'unavailable') {
    bands.push(
      <Band
        key="backlog"
        tone="gray"
        icon={<CloudOff className="h-4 w-4 shrink-0" aria-hidden="true" />}
        message={t('productBacklog.alert.backlogUnavailable')}
        next={reloadButton}
      />,
    );
  }
  if (bands.length === 0) return null;
  return <div className="mb-3 flex flex-col gap-2">{bands}</div>;
}
